import { Injectable, Injector        } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

import { SidebarRouteExtended               } from "@shared/models";
import { UserService, SessionStorageService } from "@shared/services";

import { TimeTrackingSidebarItems } from "../shared/sidebar/time-tracking-sidebar-items";
import { InvoicesSidebarItems     } from "../shared/sidebar/invoices-sidebar-items";

@Injectable({
  providedIn: 'root'
})
export class SidebarStorageService {
  private navItemsSource = new BehaviorSubject<SidebarRouteExtended[]>(null);
  navItems = this.navItemsSource.asObservable();

  private approvedReportsCountSource              = new BehaviorSubject<number>(0);
  private openActivityReportsCountSource          = new BehaviorSubject<number>(0);
  private clarificationReportsCountSource         = new BehaviorSubject<number>(0);
  private failedExportActivityReportsCountSource  = new BehaviorSubject<number>(0);

  private activePhotoDocumentsCountSource         = new BehaviorSubject<number>(0);
  private unconfirmedEBSCountSource               = new BehaviorSubject<number>(0);
  private expiringSoonAssignmentsCountSource      = new BehaviorSubject<number>(0);
  private awaitingMileageMoneyReportsCountSource  = new BehaviorSubject<number>(0);

  private approvedVacationRequestsCountSource     = new BehaviorSubject<number>(0);
  private awaitingVacationRequestsCountSource     = new BehaviorSubject<number>(0);
  private rejectedVacationRequestsCountSource     = new BehaviorSubject<number>(0);
  private failedExportVacationRequestsCountSource = new BehaviorSubject<number>(0);

  private activeInvoicesCountSource               = new BehaviorSubject<number>(0);
  private archivedInvoicesCountSource             = new BehaviorSubject<number>(0);

  private activeReferralsCountSource              = new BehaviorSubject<number>(0);
  private contractExtensionsCountSource           = new BehaviorSubject<number>(0);
  private externaEmployeesCountSource             = new BehaviorSubject<number>(0);

  approvedReportsCount              = this.approvedReportsCountSource.asObservable();
  openActivityReportsCount          = this.openActivityReportsCountSource.asObservable();
  clarificationReportsCount         = this.clarificationReportsCountSource.asObservable();
  failedExportActivityReportsCount  = this.failedExportActivityReportsCountSource.asObservable();

  activePhotoDocumentsCount         = this.activePhotoDocumentsCountSource.asObservable();
  unconfirmedEBSCount               = this.unconfirmedEBSCountSource.asObservable();
  expiringSoonAssignmentsCount      = this.expiringSoonAssignmentsCountSource.asObservable();
  awaitingMileageMoneyReportsCount  = this.awaitingMileageMoneyReportsCountSource.asObservable();

  approvedVacationRequestsCount     = this.approvedVacationRequestsCountSource.asObservable();
  awaitingVacationRequestsCount     = this.awaitingVacationRequestsCountSource.asObservable();
  rejectedVacationRequestsCount     = this.rejectedVacationRequestsCountSource.asObservable();
  failedExportVacationRequestsCount = this.failedExportVacationRequestsCountSource.asObservable();

  archivedInvoicesCount             = this.archivedInvoicesCountSource.asObservable();
  activeInvoicesCount               = this.activeInvoicesCountSource.asObservable();

  activeReferralsCount              = this.activeReferralsCountSource.asObservable();
  contractExtensionsCount           = this.contractExtensionsCountSource.asObservable();
  externaEmployeesCount             = this.externaEmployeesCountSource.asObservable();

  private sidebarStateSource = new BehaviorSubject<string>(null);
  sidebarState = this.sidebarStateSource.asObservable();

  constructor (
    private injector:    Injector,
    private userService: UserService
  ) { }

  changeApprovedReportsCount(             count: number): void { this.approvedReportsCountSource.next(count);              }
  changeOpenActivityReportsCount(         count: number): void { this.openActivityReportsCountSource.next(count);          }
  changeClarificationReportsCount(        count: number): void { this.clarificationReportsCountSource.next(count);         }
  changeFailedExportActivityReportsCount( count: number): void { this.failedExportActivityReportsCountSource.next(count);  }

  changeActivePhotoDocumentsCount(        count: number): void { this.activePhotoDocumentsCountSource.next(count);         }
  changeUnconfirmedEBSCount(              count: number): void { this.unconfirmedEBSCountSource.next(count);               }
  changeExpiringSoonAssignmentsCount(     count: number): void { this.expiringSoonAssignmentsCountSource.next(count);      }
  changeAwaitingMileageMoneyReportsCount( count: number): void { this.awaitingMileageMoneyReportsCountSource.next(count);  }

  changeApprovedVacationRequestsCount(    count: number): void { this.approvedVacationRequestsCountSource.next(count);     }
  changeAwaitingVacationRequestsCount(    count: number): void { this.awaitingVacationRequestsCountSource.next(count);     }
  changeRejectedVacationRequestsCount(    count: number): void { this.rejectedVacationRequestsCountSource.next(count);     }
  changeFailedExportVacationRequestsCount(count: number): void { this.failedExportVacationRequestsCountSource.next(count); }

  changeArchivedInvoicesCount(            count: number): void { this.archivedInvoicesCountSource.next(count);             }
  changeActiveInvoicesCount(              count: number): void { this.activeInvoicesCountSource.next(count);               }
  changeActiveReferralsCount(             count: number): void { this.activeReferralsCountSource.next(count);              }
  changeContractExtensionsCount(          count: number): void { this.contractExtensionsCountSource.next(count);           }
  changeExternaEmployeesCount(            count: number): void { this.externaEmployeesCountSource.next(count);             }

  get approvedReportsCountValue():              number { return this.approvedReportsCountSource.value;              }
  get openActivityReportsCountValue():          number { return this.openActivityReportsCountSource.value;          }
  get clarificationReportsCountValue():         number { return this.clarificationReportsCountSource.value;         }
  get failedExportActivityReportsCountValue():  number { return this.failedExportActivityReportsCountSource.value;  }

  get activePhotoDocumentsCountValue():         number { return this.activePhotoDocumentsCountSource.value;         }
  get unconfirmedEBSCountValue():               number { return this.unconfirmedEBSCountSource.value;               }
  get expiringSoonAssignmentsCountValue():      number { return this.expiringSoonAssignmentsCountSource.value;      }
  get awaitingMileageMoneyReportsCountValue():  number { return this.awaitingMileageMoneyReportsCountSource.value;  }

  get approvedVacationRequestsCountValue():     number { return this.approvedVacationRequestsCountSource.value;     }
  get awaitingVacationRequestsCountValue():     number { return this.awaitingVacationRequestsCountSource.value;     }
  get rejectedVacationRequestsCountValue():     number { return this.rejectedVacationRequestsCountSource.value;     }
  get failedExportVacationRequestsCountValue(): number { return this.failedExportVacationRequestsCountSource.value; }

  get archivedInvoicesCountValue():             number { return this.archivedInvoicesCountSource.value;             }
  get activeInvoicesCountValue():               number { return this.activeInvoicesCountSource.value;               }
  get activeReferralsCountValue():              number { return this.activeReferralsCountSource.value;              }
  get contractExtensionsCountSourceValue():     number { return this.contractExtensionsCountSource.value;           }
  get externaEmployeesCountSourceValue():       number { return this.externaEmployeesCountSource.value;             }

  countersByName(name: string):[number, (count: number) => void] {
    switch (name) {
      case 'approvedReports'                 : return [ this.approvedReportsCountValue              , this.changeApprovedReportsCount.bind(this)              ];
      case 'openActivityReports'             : return [ this.openActivityReportsCountValue          , this.changeOpenActivityReportsCount.bind(this)          ];
      case 'clarificationReports'            : return [ this.clarificationReportsCountValue         , this.changeClarificationReportsCount.bind(this)         ];
      case 'failedExportActivityReports'     : return [ this.failedExportActivityReportsCountValue  , this.changeFailedExportActivityReportsCount.bind(this)  ];
      case 'activePhotoDocuments'            : return [ this.activePhotoDocumentsCountValue         , this.changeActivePhotoDocumentsCount.bind(this)         ];
      case 'unconfirmedEBS'                  : return [ this.unconfirmedEBSCountValue               , this.changeUnconfirmedEBSCount.bind(this)               ];
      case 'expiringSoonAssignments'         : return [ this.expiringSoonAssignmentsCountValue      , this.changeExpiringSoonAssignmentsCount.bind(this)      ];
      case 'awaitingMileageMoneyReports'     : return [ this.awaitingMileageMoneyReportsCountValue  , this.changeAwaitingMileageMoneyReportsCount.bind(this)  ];
      case 'approvedVacationRequests'        : return [ this.approvedVacationRequestsCountValue     , this.changeApprovedVacationRequestsCount.bind(this)     ];
      case 'awaitingVacationRequests'        : return [ this.awaitingVacationRequestsCountValue     , this.changeAwaitingVacationRequestsCount.bind(this)     ];
      case 'rejectedVacationRequests'        : return [ this.rejectedVacationRequestsCountValue     , this.changeRejectedVacationRequestsCount.bind(this)     ];
      case 'failedExportVacationRequests'    : return [ this.failedExportVacationRequestsCountValue , this.changeFailedExportVacationRequestsCount.bind(this) ];
      case 'archivedInvoices'                : return [ this.archivedInvoicesCountValue             , this.changeArchivedInvoicesCount.bind(this)             ];
      case 'activeInvoices'                  : return [ this.activeInvoicesCountValue               , this.changeActiveInvoicesCount.bind(this)               ];
      case 'activeReferrals'                 : return [ this.activeReferralsCountValue              , this.changeActiveReferralsCount.bind(this)              ];
      case 'activeContractExt'               : return [ this.contractExtensionsCountSourceValue     , this.changeContractExtensionsCount.bind(this)           ];
      default:
        throw new Error(`Unknown counter name: ${name}`);
    }
  }

  changeNavItems(navItems: SidebarRouteExtended[]): void {
    this.navItemsSource.next(navItems);
  }

  prepareTimeTrackingSidebar(): void {
    let tempNavItems = TimeTrackingSidebarItems.filter(item => this.filterByRole(item))
    .map(item => {
      if (item.children) item.children = item.children.filter(child => this.filterByRole(child)).filter(i => {
        let features = this.userService.currentUserValue?.features[i.betaFlag];
        if (!i.betaFlag) return true;
        return features && (features === true || features.length);
      });
      return item;
    });
    tempNavItems = this.highlightNewFeatures(tempNavItems);
    this.changeNavItems(tempNavItems);
  }

  private filterByRole(item): boolean {
    if (!item.isCustomer && !item.isInternal) return true;
    else if (item.isCustomer) return item.isCustomer && this.userService.isCustomer;
    else if (item.isInternal) return item.isInternal && this.userService.isInternal;
  }

  private highlightNewFeatures(navItems: SidebarRouteExtended[]): SidebarRouteExtended[] {
    let sessionStorageService = this.injector.get(SessionStorageService);
    let list = navItems.map(i => ({...i}));

    if (this.userService.latestRelease && +(new Date(this.userService.latestRelease.released_at)) < +new Date() + 7*24*60*60*1000 && !sessionStorageService.checkReleasePassed(this.userService.latestRelease)) this.highlightFeature(list, 'releases');
    return list;
  }

  private highlightFeature(list: SidebarRouteExtended[], feature: string): void {
    let nav = list.find(i => i.path === feature);
    if (nav) nav.dot = true;
  }

  prepareInvoicesSidebar(): void {
    this.changeNavItems(InvoicesSidebarItems);
  }

  changeSidebar(app: string = null): void {
    if (!app || app === 'time-tracking') this.prepareTimeTrackingSidebar();
    else if    (app === 'invoices')      this.prepareInvoicesSidebar();
  }

  get sidebarItems(): SidebarRouteExtended[] {
    return this.navItemsSource.value;
  }

  get sidebarItemsSubscribe(): Observable<SidebarRouteExtended[]> {
    return this.navItems;
  }

  get navItemsMapped(): SidebarRouteExtended[] {
    let parent   = this.sidebarItems;
    let children = [...this.sidebarItems.filter(i => i.children).reduce((sum, val) => sum = [...sum, ...val.children], [])];
    return [...parent, ...children];
  }

  changeSidebarState(state: string): void {
    this.sidebarStateSource.next(state);
  }

  toggleSidebarState(): void {
    this.sidebarStateSource.next(this.sidebarStateSource.value === 'mini' ? 'full' : 'mini');
  }

}
